import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import GoogleMapReact from 'google-map-react';
import {
    SoilNewReactIcon,
    SoilProfileMapCurrentLocationIcon,
    SoilProfileZoomIn,
    SoilProfileZoomOut,
    SoilSearchAddPolyLinePoint,
    SoilSearchCircleIcon,
    SoilSearchClearPolyIcon,
    SoilSearchiCircleSelectedIcon,
    SoilSearchProfileCount,
    WeatherIndicateMark,
} from '@/resource/IconComponentSets';
import useSupercluster from 'use-supercluster';
import IF from '@/components/IF';
import { RootState } from '@reduxjs/toolkit';
import './index.less';
import { IWeatherMapStationResModel, IWeatherStationModel } from '@/util/Request/requestType';
import WeatherSunnyNormalSvg from './Component/WeatherSunnyNormalSvg';
import { weatherGetHierarchy } from '@/util/Request';
import { updateDrawOnMapData, updateWeatherHierchydata } from '@/redux/reducer/weatherStation';

interface ICoordinate {
    lat: number
    lng: number
}

interface IProps {
    mapData: IWeatherMapStationResModel[]
    polyPathCallback?: (pathPoint: ICoordinate[]) => void
}

interface ITypeInclude {
    // Arable: boolean
    // Devis: boolean
    // GYGA: boolean
    // Meteoblue: boolean
    // Pessl: boolean
    // Sencrop: boolean
    // 'Adama(Pessl)': boolean
    // PairTree: boolean
    // Weenat: boolean
    // WeatherMeasures: boolean
    [typeInclude: string]: boolean
}

const colorLiteral = {
    Arable: '#9664F0',
    Davis: '#EE5B3A',
    GYGA: '#8C63A4',
    Meteoblue: '#0092E4',
    Pessl: '#F9F400',
    Sencrop: 'orange',
    'Adama(Pessl)': '#E33539',
    PairTree: 'blue',
    Weenat: 'purple',
    WeatherMeasures: 'red',
};

const GoogleMapReactComponent = (props: IProps) => {
    const mapRef = useRef(null);
    const mapsRef = useRef(null);
    const polyLineSetRef = useRef([]);
    const polygonRef = useRef(null);
    const polygonCircleRef = useRef(null);

    const defaultCenter = {
        lat: 10.99835602,
        lng: 77.01502627
    };

    const {
        mapData,
        polyPathCallback,
    } = props;

    const dispatch = useDispatch();
    const {
        drawReactModel,
        drawCircleMode,
        polyPoint,
        fromQueryHistory,
        center,
        radius,
        weatherHierchyData
    } = useSelector((state: RootState) => state.weatherStation);

    // const [drawReactModel, setDrawReactModel] = useState(true);
    // const [drawCircleMode, setDrawCircleMode] = useState(false);
    const [polygonExist, setPolygonExist] = useState(false);
    const [polylinePoint, setPolylinePoint] = useState<ICoordinate[]>([]);
    const [polyLinePointsComponent, setPolyLinePointsComponent] = useState<React.ReactNode>([]);
    const [typeInclude, setTypeInclude] = useState<ITypeInclude>({});
    const [typeIncludeShow, setTypeIncludeShow] = useState(true);
    const [bounds, setBounds] = useState(null);
    const [zoom, setZoom] = useState(10);
    const [points, setPoints] = useState([]);
    const [chartVisible, setChartVisible] = useState(false);
    const [mapLoadFinish, setMapLoadFinish] = useState(false);

    const { polyFuncEnable } = useSelector((state: RootState) => state.weatherStation);


    // SECTION: Function area =====================================================================================
    const handleGoogleApiLoaded = useCallback(({ map, maps }) => {
        mapRef.current = map;
        mapsRef.current = maps;
        setMapLoadFinish(true);
    }, [drawCircleMode]);

    useEffect(() => {
        handleShowCurrentLocation();
        handleGetAllSupplier();
    }, []);

    const handleGetAllSupplier = async () => {
        let data;
        if (weatherHierchyData) {
            data = weatherGetHierarchy;
        } else {
            data = await weatherGetHierarchy();
            dispatch(updateWeatherHierchydata(data));
        }
        const newTypeInclude: ITypeInclude = {};
        if (data.metadata) {
            data.metadata.supplier.map((supplier) => {
                newTypeInclude[supplier] = false;
            });
            setTypeInclude(newTypeInclude);
        }
    };

    useEffect(() => {
        if (fromQueryHistory && mapLoadFinish) {
            if (drawReactModel && polyLineSetRef.current && polyPoint) {
                let newPolyPoint;
                if (polygonExist) {
                // add latest point to the line set
                    newPolyPoint = [];
                    polyLineSetRef.current.forEach(polyLine => {
                        polyLine.setMap(null);
                    });
                    polygonRef.current.setMap(null);
                    polyLineSetRef.current = [];
                }
                const firstPoint = polyPoint[0];
                newPolyPoint = [...polyPoint];
                newPolyPoint.push(firstPoint);

                const geodesicPolyline = new mapsRef.current.Polyline({
                    path: newPolyPoint,
                    geodesic: false,
                    strokeColor: 'orange',
                    strokeOpacity: 0.7,
                    strokeWeight: 2,
                });
                const geodesicPolygon = new mapsRef.current.Polygon({
                    paths: polyPoint,
                    strokeColor: 'orange',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: 'white',
                    fillOpacity: 0.35
                });
                if (polyLineSetRef.current && polyLineSetRef.current.length == 0) {
                    polyLineSetRef.current = [geodesicPolyline];
                } else {
                    polyLineSetRef.current.push(geodesicPolyline);
                }
                polygonRef.current = geodesicPolygon;
                geodesicPolyline.setMap(mapRef.current);
                geodesicPolygon.setMap(mapRef.current);
                setPolylinePoint(polyPoint);
                // handleDrawPolygon({ lat: firstPoint.lat, lng: firstPoint.lng });
                dispatch(updateDrawOnMapData({ fromQueryHistory: false }));
            }

            if (drawCircleMode) {
                polygonCircleRef.current?.setMap(null);
                const polygonCircle = new mapsRef.current.Circle({
                    strokeColor: 'orange',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    fillColor: '#FFFFFF',
                    fillOpacity: 0.1,
                    map: mapRef.current,
                    center: {lat: center.lat, lng: center.lng},
                    radius: radius || 10000,
                    editable: true
                });
                mapsRef.current.event.addListener(polygonCircle, 'radius_changed', function() {
                    console.log(polygonCircle.getRadius());
                    const radius = polygonCircle.getRadius();
                    const center = polygonCircle.getCenter();
                    const lat = center.lat;
                    const lng = center.lng;
                    // 10000 lat translate
                    const bottomLat = lat - radius / 110000;
                    const topLat = lat + radius / 110000;
                    const leftLng = lng + radius / 100000;
                    const rightLng = lng - radius / 100000;
                    const top = {lat: topLat, lng};
                    const left = { lat, lng: leftLng};
                    const bottom = {lat: bottomLat, lng};
                    const right = { lat, lng: rightLng };
                    const pathPoint = [top, left, bottom, right];

                    // polyPathCallback(pathPoint);
                    dispatch(updateDrawOnMapData({
                        circlePoint: pathPoint,
                        center,
                        radius
                    }));
                });
                polygonCircleRef.current = polygonCircle;
                dispatch(updateDrawOnMapData({ fromQueryHistory: false }));
            }
            setPolygonExist(true);

        }
    }, [fromQueryHistory, mapLoadFinish]);

    const handleDrawPolygon = useCallback((event?) => {
        if (polylinePoint.length > 2 && !polygonExist) {
            mapClick(event);

            const geodesicPolygon = new mapsRef.current.Polygon({
                paths: polylinePoint,
                strokeColor: 'orange',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: 'white',
                fillOpacity: 0.35
            });
            geodesicPolygon.setMap(mapRef.current);
            setPolygonExist(true);
            polygonRef.current = geodesicPolygon;
            // pass the polygon point path to outter
            const newPolylinePoint = [...polylinePoint, polylinePoint[0]];
            polyPathCallback && polyPathCallback(newPolylinePoint);
        }
    }, [polylinePoint]);

    const handleShowCurrentLocation = () => {
        navigator.geolocation.getCurrentPosition(
            (position: GeolocationPosition) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };
                if (mapRef && mapRef.current) {
                    mapRef.current.setCenter(pos);
                }
            },
            () => {
                console.log('test get current location error');
            }
        );
    };

    const mapClick = (event) => {
        if (polygonExist) {
            clearAll();
        }
        // if current tab is not at draw on map draw rect or circle is enable
        if (!polyFuncEnable) {
            return;
        }

        if (drawCircleMode) {
            // if draw the circle should clear circle add before
            polygonCircleRef.current?.setMap(null);
            const localZoom = 23 - zoom;
            const radius = localZoom * Math.pow(localZoom < 7 ? 10 : 100, (Math.round(localZoom / 10)));
            const lat = event.lat;
            const lng = event.lng;
            const polygonCircle = new mapsRef.current.Circle({
                strokeColor: 'orange',
                strokeOpacity: 1,
                strokeWeight: 2,
                fillColor: 'white',
                fillOpacity: 0.1,
                map: mapRef.current,
                center: {lat, lng},
                radius,
                editable: true
            });
            const bottomLat = event.lat - radius / 110000;
            const topLat = lat + radius / 110000;
            const leftLng = lng + radius / 110000;
            const rightLng = lng - radius / 110000;
            const top = {lat: topLat, lng};
            const left = { lat, lng: leftLng};
            const bottom = {lat: bottomLat, lng};
            const right = { lat, lng: rightLng };
            const pathPoint = [top, left, bottom, right];
            polyPathCallback(pathPoint);

            mapsRef.current.event.addListener(polygonCircle, 'radius_changed', () => {
                console.log(polygonCircle.getRadius());
                const radius = polygonCircle.getRadius();
                const center = polygonCircle.getCenter();
                const lat = center.lat();
                const lng = center.lng();
                // 10000 lat translate
                const bottomLat = lat - radius / 110000;
                const topLat = lat + radius / 110000;
                const leftLng = lng + radius / 110000;
                const rightLng = lng - radius / 110000;
                const top = {lat: topLat, lng};
                const left = { lat, lng: leftLng};
                const bottom = {lat: bottomLat, lng};
                const right = { lat, lng: rightLng };
                const pathPoint = [top, left, bottom, right];
                dispatch(updateDrawOnMapData({
                    center,
                    radius
                }));
                polyPathCallback(pathPoint);
            });

            mapsRef.current.event.addListener(polygonCircle, 'center_changed', () => {
                console.log(polygonCircle.getRadius());
                const radius = polygonCircle.getRadius();
                const center = polygonCircle.getCenter();
                const lat = center.lat();
                const lng = center.lng();
                // 10000 lat translate
                const bottomLat = lat - radius / 110000;
                const topLat = lat + radius / 110000;
                const leftLng = lng + radius / 110000;
                const rightLng = lng - radius / 110000;
                const top = {lat: topLat, lng};
                const left = { lat, lng: leftLng};
                const bottom = {lat: bottomLat, lng};
                const right = { lat, lng: rightLng };
                const pathPoint = [top, left, bottom, right];
                polyPathCallback(pathPoint);
                dispatch(updateDrawOnMapData({
                    center,
                    radius
                }));
            });

            return polygonCircleRef.current = polygonCircle;
        }

        if (drawReactModel) {
            let newPolyPoint;
            if (polygonExist) {
            // add latest point to the line set
                newPolyPoint = [];
                setPolygonExist(false);
                polyLineSetRef.current.forEach(polyLine => {
                    polyLine.setMap(null);
                });
                polygonRef.current.setMap(null);
                polyLineSetRef.current = [];
            } else {
                newPolyPoint = [...polylinePoint];
            }
            const currentPoint = { lat: event.lat, lng: event.lng };
            newPolyPoint.push(currentPoint);
            setPolylinePoint(newPolyPoint);

            // draw polyline
            const geodesicPolyline = new mapsRef.current.Polyline({
                path: newPolyPoint,
                geodesic: false,
                strokeColor: 'orange',
                strokeOpacity: 0.7,
                strokeWeight: 2
            });
            dispatch(updateDrawOnMapData({ polyPoint: newPolyPoint }));
            geodesicPolyline.setMap(mapRef.current);
            if (polyLineSetRef.current && polyLineSetRef.current.length == 0) {
                polyLineSetRef.current = [geodesicPolyline];
            } else {
                polyLineSetRef.current.push(geodesicPolyline);
            }
        }
    };

    const clearAll = () => {
        // clear all line here
        setPolylinePoint([]);
        // clear all line
        polyLineSetRef.current?.forEach(polyLineRef => {
            polyLineRef.setMap(null);
        });
        // clear the react
        polygonRef.current?.setMap(null);

        setPolygonExist(false);
        polygonCircleRef.current?.setMap(null);
    };

    // SECTION: useEffect area =====================================================================================
    useEffect(() => {
        const polyPointSets = polylinePoint.map(((item, index) => {
            if (index === 0) {
                return (
                    <SoilSearchAddPolyLinePoint
                        key={'first'}
                        style={{
                            position: 'relative',
                            top: '-14px',
                            right: '14px',
                            display: 'flex',
                            width: '30px',
                            height: '30px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: 'pointer',
                            zIndex: '999',
                        }}
                        lat={item.lat}
                        lng={item.lng}
                        onClick={() => handleDrawPolygon(item)}
                    />
                );
            }

            return (
                <SoilSearchAddPolyLinePoint
                    key={`${item.lat}-${item.lng}`}
                    style={{
                        position: 'relative',
                        top: '-8px',
                        right: '8px'
                    }}
                    lat={item.lat}
                    lng={item.lng}
                />
            );
        } ));
        setPolyLinePointsComponent(polyPointSets);
    }, [polylinePoint]);

    const { clusters } = useSupercluster({
        points,
        bounds,
        zoom,
        options: { radius: 75, maxZoom: 15 }
    });

    useEffect(() => {
        if (drawCircleMode) {
            // clear all line here
            setPolylinePoint([]);
            // clear all line
            polyLineSetRef.current?.forEach(polyLineRef => {
                polyLineRef.setMap(null);
            });
            // clear the react
            polygonRef.current?.setMap(null);
            setPolygonExist(false);
        }
        if (drawReactModel) {
            polygonCircleRef.current?.setMap(null);
        }
    }, [drawCircleMode, drawReactModel]);

    useEffect(() => {
        if (!polyFuncEnable) {
            clearAll();
        }
    }, [polyFuncEnable]);

    useEffect(() => {
        if (!mapData) {
            setPoints([]);
            setTypeInclude({});
            return;
        }
        const stations: IWeatherStationModel[] = [];
        const stationIdArr: Array<string> = [];
        const newTypeInclude = {};
        const pointArr = [];
        let center;
        for (const mapIterator of mapData) {
            newTypeInclude[mapIterator.supplier] = true;
            for (const station of mapIterator.stations) {
                if (!center) {
                    center = {
                        lat: parseFloat(station.latitude),
                        lng: parseFloat(station.longitude)
                    };
                }
                if (!stationIdArr.includes(station.stationID)) {
                    stationIdArr.push(station.stationID);
                } else {
                    // if station have exist, don't push it to point arr
                    continue;
                }
                stations.push(station);
                const point = {
                    type: 'Feature',
                    properties: {
                        cluster: false,
                        crimeId: `${station.stationID}-${station.latitude}-${station.longitude}`,
                        category: 'weather-social-behaviour',
                        stationId: station.stationID
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [
                            station.longitude,
                            station.latitude
                        ],
                        annotationType: station.supplier
                    }
                };
                pointArr.push(point);
            }
        }
        setPoints(pointArr);
        setTypeInclude(newTypeInclude);
        mapRef?.current?.setCenter(center);
    }, [mapData]);

    // SECTION: render ui area =====================================================================================
    return (
        <Spin spinning={false}>
            <div className={'weather-map-view-container'}>
                <div className='weather-map-component-container'>
                    <GoogleMapReact
                        style={{ height: 'calc(100vh - 122px)'}}
                        onChange={({ zoom, bounds }) => {
                            setZoom(zoom);
                            setBounds([
                                bounds.nw.lng,
                                bounds.se.lat,
                                bounds.se.lng,
                                bounds.nw.lat
                            ]);
                        }}
                        bootstrapURLKeys={{
                            key: 'AIzaSyDlsxkoR8-fiIgncnDunmEtWEwDmr896H0',
                            libraries: ['drawing']
                        }}
                        options={(maps) => {
                            return {
                                // mapTypeId: maps.MapTypeId.SATELLITE,
                                fullscreenControl: false
                            };
                        }}
                        yesIWantToUseGoogleMapApiInternals={true}
                        defaultCenter={defaultCenter}
                        // center={selectedCoordinate}
                        defaultZoom={1}
                        onGoogleApiLoaded={handleGoogleApiLoaded}
                        onClick={mapClick}
                    >
                        {polyLinePointsComponent}
                        {
                            clusters.length > 0 && clusters.map((cluster, index) => {
                                const [longitude, latitude] = cluster.geometry.coordinates;
                                const {
                                    cluster: isCluster,
                                    point_count,
                                    stationId
                                } = cluster.properties;
                                const {
                                    annotationType
                                } = cluster.geometry;
                                if (isCluster) {
                                    return (
                                        <SoilSearchProfileCount
                                            key={`cluster-${cluster.id}`}
                                            lat={latitude}
                                            lng={longitude}
                                            className='weather-marker-annotation-count'
                                            onClick={() => {
                                                let zoom = mapRef.current.getZoom();
                                                mapRef.current.setZoom(zoom+=1);
                                            }}
                                        >
                                            <span
                                                className={point_count > 9 ? 'weather-point-count-more-than-99-span' : 'point-count-span'}
                                            >
                                                {point_count > 99 ? '99+' : point_count}
                                            </span>
                                        </SoilSearchProfileCount>
                                    );
                                } else {
                                    return (
                                        <WeatherSunnyNormalSvg
                                            lat={latitude}
                                            lng={longitude}
                                            key={`${stationId}-${index}`}
                                            color={colorLiteral[annotationType]}
                                            className='weather-marker-annotation-count'
                                            stationId={stationId}
                                            chartVisibleCallBack={(chartVisible: boolean) => setChartVisible(chartVisible)}
                                        />
                                    );
                                }
                            })
                        }
                    </GoogleMapReact>
                    <div className='supplier-type-menu'>
                        <div className='operation-title-div'>
                            <span className='supplier-span'>
                                Supplier on map
                            </span>
                            <button
                                className='hide-or-open-button'
                                onClick={() => setTypeIncludeShow(!typeIncludeShow)}
                            >
                                <span>
                                    {!chartVisible && typeIncludeShow ? 'hide' : 'show'}
                                </span>
                                <span className='show-hide-span'>
                                    {!chartVisible && typeIncludeShow ? '-' : '+'}
                                </span>
                            </button>
                        </div>
                        <IF show={!chartVisible && typeIncludeShow}>
                            <div className='supplier-type-indicate-div'>
                                <div className='supplier-div'>
                                    {
                                        Object.keys(typeInclude).map((supplier) => {
                                            return typeInclude[supplier] || !mapData ? (
                                                <div className='supplier-common-div' key={supplier}>
                                                    <div className={`background-div ${typeInclude[supplier] && 'white-border'}`}>
                                                        <div className='round-div' style={{ background: colorLiteral[supplier] || 'red' }} />
                                                    </div>
                                                    <span className='supplier-text'>
                                                        {supplier}
                                                    </span>
                                                </div>
                                            ) : null;
                                        })
                                    }
                                </div>
                                <div className='bottom-indicate-button'>
                                    <WeatherIndicateMark />
                                    <span>What it shows</span>
                                </div>
                            </div>
                        </IF>
                    </div>
                    <button className='query-toggle-map-center' onClick={() => console.log('test!!!')}>
                        <SoilProfileMapCurrentLocationIcon />
                    </button>
                    <div className={'zoom-action-div left-hand'}>
                        <button className='zoom-in-button' onClick={() => {
                            let zoom = mapRef.current.getZoom();
                            mapRef.current.setZoom(zoom-=1);
                        }}>
                            <SoilProfileZoomIn />
                        </button>
                        <button
                            className='zoom-out-button'
                            onClick={() => {
                                let zoom = mapRef.current.getZoom();
                                mapRef.current.setZoom(zoom+=1);
                            }}>
                            <SoilProfileZoomOut />
                        </button>
                    </div>
                    <IF show={polyFuncEnable}>
                        <div className='google-map-operation-menu'>
                            <div>
                                <button onClick={() => {
                                    // setDrawCircleMode(false);
                                    // setDrawReactModel(!drawReactModel);
                                    dispatch(updateDrawOnMapData({
                                        drawCircleMode: false,
                                        drawReactModel: true
                                    }));
                                }}>
                                    <SoilNewReactIcon selected={drawReactModel} />
                                </button>
                            </div>
                            <div>
                                <button onClick={() => {
                                    // setDrawReactModel(false);
                                    // setDrawCircleMode(!drawCircleMode);
                                    dispatch(updateDrawOnMapData({
                                        drawCircleMode: true,
                                        drawReactModel: false
                                    }));
                                }}>
                                    <IF show={drawCircleMode}>
                                        <SoilSearchiCircleSelectedIcon />
                                    </IF>
                                    <IF show={!drawCircleMode}>
                                        <SoilSearchCircleIcon />
                                    </IF>
                                </button>
                            </div>
                            <div>
                                <button onClick={clearAll}>
                                    <SoilSearchClearPolyIcon />
                                </button>
                            </div>
                        </div>
                    </IF>
                </div>
            </div>
        </Spin>
    );
};

export type {
    ICoordinate
};

export default GoogleMapReactComponent;